import { useState } from 'react';
import { P2GBox, P2GCollapse, P2GGrid, P2GLink, P2GText } from '@parcel2go/core-ui';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { AppCard } from '~/components/card/app-card';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import SelectOrCopy from '~/apps/smartsend/components/smartsend/item/SelectOrCopy';
import { ArchivedOrder } from './types';
import { ArchivedOrderDetails } from './ArchivedOrderDetails';

interface props {
  order: ArchivedOrder;
}

export const ArchivedOrderCard: React.FC<props> = ({ order }) => {
  const { t } = useTranslation(TranslationNamespaces.MYORDERS);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <AppCard>
      <P2GBox p={2}>
        <P2GGrid container spacing={1} alignItems="center" wrap="nowrap">
          <P2GGrid item flexGrow={1}>
            <P2GText component="div" fontWeight="bold">
              {order.ReferenceNumber}
            </P2GText>
            <P2GText component="div" variant="body2">
              {order.ServiceName}
            </P2GText>
            <P2GText component="div" variant="body2">
              {order.DeliveryName}
            </P2GText>
            <P2GText component="div" variant="body2">
              {order.Description}
            </P2GText>
            {(order.TrackingNumber || order.TrackingUrl) && (
              <P2GBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                {order.TrackingNumber && (
                  <>
                    <P2GText component="span" variant="body2">
                      {t('ARCHIVED_ORDERS_TRACKING_PREFIX', {
                        defaultValue: 'Tracking: {{tracking}}',
                        tracking: order.TrackingNumber
                      })}
                    </P2GText>
                    <SelectOrCopy code={order.TrackingNumber} variant="body2" />
                  </>
                )}
                {order.TrackingUrl && (
                  <P2GLink href={order.TrackingUrl} target="_blank" variant="body2">
                    {t('ARCHIVED_ORDERS_TRACK_LINK', { defaultValue: 'Track' })}
                  </P2GLink>
                )}
              </P2GBox>
            )}
          </P2GGrid>
          <P2GGrid item>
            <P2GText component="div" variant="body1">
              {toDisplayCurrency(undefined, order.TotalIncVat)}
            </P2GText>
          </P2GGrid>
        </P2GGrid>
        <P2GBox mt={2}>
          <P2GLink onClick={() => setIsExpanded((current) => !current)}>
            {isExpanded
              ? t('ARCHIVED_ORDERS_HIDE_LINK', { defaultValue: 'Hide' })
              : t('ARCHIVED_ORDERS_VIEW_LINK', { defaultValue: 'View' })}
          </P2GLink>
        </P2GBox>
        <P2GCollapse in={isExpanded} unmountOnExit>
          <ArchivedOrderDetails order={order} />
        </P2GCollapse>
      </P2GBox>
    </AppCard>
  );
};
